import { attach, createEffect, createEvent, createStore } from 'effector';
import { AxiosProgressEvent, Canceler } from 'axios';
import { uploadBucketAttachment } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { createInitialEntityState, EntityState, UploadFile, UploadFileStatus } from '@nx-frontend/shared/types';

export interface FilesUploadingState extends EntityState<UploadFile> {
  isUploadModalCollapsed: boolean;
}

export const filesUploadingInitialState = {
  ...createInitialEntityState<UploadFile>(),
  isUploadModalCollapsed: false,
};

export const startUploadFileEvent = createEvent<UploadFile>();
export const updateUploadFileEvent = createEvent<{ id: string; uploadedSize: number; size?: number }>();
export const endUploadFileEvent = createEvent<{ id: string; status: UploadFileStatus }>();
export const deleteUploadFileEvent = createEvent<string>();
export const clearFilesUploadingStoreEvent = createEvent<void>();
export const toggleCollapseUploadModalEvent = createEvent<void>();

export interface UploadFileParams {
  file: File;
  sessionId: string;
  workspaceId: string;
  groupId: string;
  target: string;
  targetId: string;
  id: string;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  getCancelToken?: (canceler: Canceler) => void;
}

export const uploadFileFx = createEffect(
  async ({
    sessionId,
    workspaceId,
    groupId,
    target,
    targetId,
    id,
    file,
    onUploadProgress,
    getCancelToken,
  }: UploadFileParams) => {
    return await uploadBucketAttachment({
      gatewayUrl: generalEnv.gatewayUrl,
      id,
      sessionId,
      workspaceId,
      groupId,
      target,
      targetId,
      file,
      onUploadProgress,
      getCancelToken,
    });
  }
);

export const $filesUploadingState = createStore<FilesUploadingState>(filesUploadingInitialState)
  .on(startUploadFileEvent, (state, file) => {
    const entities = { ...state.entities };
    entities[file.id] = { ...file };
    return {
      ...state,
      entities,
      ids: getSortedEntitiesIds(entities),
    };
  })
  .on(updateUploadFileEvent, (state, { id, uploadedSize, size }) => {
    const entities = { ...state.entities };
    const item = { ...entities[id] };
    if (item) {
      entities[id] = { ...item, id, uploadedSize, size: size ?? item.size };
    }
    return { ...state, entities: { ...entities }, ids: getSortedEntitiesIds(entities) };
  })
  .on(endUploadFileEvent, (state, { id, status }) => {
    const entities = { ...state.entities };
    const item = entities[id];
    if (item) {
      entities[id] = { ...item, status, uploadedSize: item.size };
    }
    return { ...state, entities: { ...entities }, ids: getSortedEntitiesIds(entities) };
  })
  .on(deleteUploadFileEvent, (state, id) => {
    const entities = { ...state.entities };
    if (entities[id]) {
      delete entities[id];
    }
    return { ...state, entities: { ...entities }, ids: getSortedEntitiesIds(entities) };
  })
  .on(toggleCollapseUploadModalEvent, (state) => {
    return { ...state, isUploadModalCollapsed: !state.isUploadModalCollapsed };
  })
  .on(clearFilesUploadingStoreEvent, () => {
    return filesUploadingInitialState;
  });

export const $uploadFileIds = $filesUploadingState.map((state) => state.ids);
export const $allFilesUploaded = $filesUploadingState.map((state) =>
  Object.values(state.entities).every(
    (file) =>
      file.status === UploadFileStatus.DONE ||
      file.status === UploadFileStatus.CANCELED ||
      file.status === UploadFileStatus.ERROR
  )
);
export const $isUploadModalCollapsed = $filesUploadingState.map((state) => state.isUploadModalCollapsed);

export const cancelUploadingProgressFilesFx = attach({
  source: [$filesUploadingState],
  effect: ([state]: [FilesUploadingState], params?: { targetId?: string }) => {
    const entities = state.entities;
    const filesToCancel = Object.values(entities).filter(
      (file) =>
        file.status === UploadFileStatus.IN_PROGRESS &&
        file.canceler &&
        (!params?.targetId || (params?.targetId && params?.targetId === file.targetId))
    );
    filesToCancel.forEach((file) => {
      if (file.canceler) {
        file.canceler();
      }
    });
  },
});
export function getUploadFileByIdParams(id: string) {
  return {
    keys: [id],
    store: $filesUploadingState,
    fn: (state: FilesUploadingState, [keyId]: string[]) => state.entities[keyId],
    updateFilter: (prev: UploadFile | undefined, next: UploadFile | undefined) =>
      prev?.uploadedSize !== next?.uploadedSize || prev?.status !== next?.status,
  };
}

export const $uploadProgress = $filesUploadingState.map((state) => {
  const items = Object.values(state.entities);
  return items.reduce(
    (acc, item) => {
      acc.filesCount++;
      acc.bytesCount += item.size;
      acc.doneBytesCount += item.uploadedSize;
      if (item.status === UploadFileStatus.DONE || item.status === UploadFileStatus.CANCELED) {
        acc.doneFilesCount++;
      }
      return acc;
    },
    {
      filesCount: 0,
      doneFilesCount: 0,
      bytesCount: 0,
      doneBytesCount: 0,
    }
  );
});

function getSortedEntitiesIds(entities: Record<string, UploadFile>) {
  return Object.values(entities)
    .sort((a, b) => b.createdAt - a.createdAt)
    .map((file) => file.id);
}

export function getUploadingFileIdsByTargetId(targetId: string) {
  return {
    keys: [targetId],
    store: $filesUploadingState,
    defaultValue: [],
    fn: (state: FilesUploadingState, [targetId]: string[]): string[] =>
      Object.values(state.entities)
        .filter((file) => file.targetId === targetId)
        .sort((a, b) => b.createdAt - a.createdAt)
        .map((file) => file.id),
  };
}

export function getAllFilesUploadedByTargetId(targetId: string) {
  return {
    keys: [targetId],
    store: $filesUploadingState,
    defaultValue: [],
    fn: (state: FilesUploadingState, [targetId]: string[]): boolean =>
      Object.values(state.entities)
        .filter((file) => targetId === file.targetId)
        .every(
          (file) =>
            (!targetId || (targetId && targetId === file.targetId)) &&
            (file.status === UploadFileStatus.DONE ||
              file.status === UploadFileStatus.CANCELED ||
              file.status === UploadFileStatus.ERROR)
        ),
  };
}
